/**
 * The data layer of a `waves` campaign (B2G): outreach waves, the send plan,
 * markets and suppressions from `api/b2g.ts`, plus the owner's writes on
 * them. Every mutation invalidates the `["b2g"]` family and the console's,
 * so funnels and needs-you repaint together.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { B2G_KEY, b2gApi, type Market, type Wave } from "../../api/b2g";
import { CONSOLE_KEY } from "../../api/console";
import type { MarketDecision } from "../../components/b2g/MarketsTable";
import type { WaveAction } from "../../components/b2g/WaveDetail";
import { useToast } from "../../components/toast/context";
import { letterBlock } from "../../lib/b2g";

const POLL_MS = 30_000;
/** While a letter waits for the owner's signature: the seat attaches the signed file soon after the save. */
const SIGNATURE_POLL_MS = 5_000;

const ACTION_TOAST: Record<WaveAction, string> = {
  approve: "Approved – the server sends inside business hours.",
  approve_drafts: "Approved as drafts – they appear in the outreach mailbox's Drafts folder within a minute.",
  request_changes: "Sent back to the seat with your note.",
  reject: "Rejected – the seat reads your note.",
  pause: "Wave paused.",
  resume: "Wave resumed.",
  stop: "Wave stopped – nothing more goes out.",
};

export function useWavesData({ enabled, waveId, tab }: { enabled: boolean; waveId: string | null; tab: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const waves = useQuery({
    queryKey: [...B2G_KEY, "waves"],
    queryFn: () => b2gApi.waves(),
    enabled,
    refetchInterval: POLL_MS,
  });
  const wave = useQuery({
    queryKey: [...B2G_KEY, "wave", waveId],
    queryFn: () => b2gApi.wave(waveId as string),
    enabled: enabled && !!waveId,
    refetchInterval: (query) => (letterBlock(query.state.data?.messages ?? []) ? SIGNATURE_POLL_MS : POLL_MS),
  });
  const plan = useQuery({ queryKey: [...B2G_KEY, "plan"], queryFn: b2gApi.plan, enabled, refetchInterval: POLL_MS });
  const markets = useQuery({
    queryKey: [...B2G_KEY, "markets"],
    queryFn: () => b2gApi.markets(),
    enabled: enabled && tab === "markets",
    refetchInterval: POLL_MS,
  });
  const suppressions = useQuery({
    queryKey: [...B2G_KEY, "suppressions"],
    queryFn: b2gApi.suppressions,
    enabled: enabled && tab === "dnc",
  });

  const invalidate = () =>
    Promise.all([qc.invalidateQueries({ queryKey: B2G_KEY }), qc.invalidateQueries({ queryKey: CONSOLE_KEY })]);

  const waveAction = useMutation({
    mutationFn: async ({ w, action, note }: { w: Wave; action: WaveAction; note?: string }) => {
      switch (action) {
        case "approve":
          return b2gApi.approve(w.id, w.content_hash);
        case "approve_drafts":
          return b2gApi.approve(w.id, w.content_hash, "drafts");
        case "request_changes":
          return b2gApi.requestChanges(w.id, note ?? "");
        case "reject":
          return b2gApi.reject(w.id, note);
        case "pause":
          return b2gApi.pause(w.id);
        case "resume":
          return b2gApi.resume(w.id);
        case "stop":
          return b2gApi.stop(w.id, note);
      }
    },
    onMutate: ({ w }) => setBusy(w.id),
    onSettled: () => setBusy(null),
    onSuccess: (_d, { action }) => {
      toast.ok(ACTION_TOAST[action]);
      return invalidate();
    },
    onError: (err) => {
      toast.error(err);
      return invalidate();
    },
  });

  const decide = useMutation({
    mutationFn: ({ m, d }: { m: Market; d: MarketDecision }) => b2gApi.marketDecision(m.iso2, d),
    onMutate: ({ m }) => setBusy(m.iso2),
    onSettled: () => setBusy(null),
    onSuccess: () => {
      toast.ok("Market decision saved.");
      return invalidate();
    },
    onError: (err) => toast.error(err),
  });

  const lift = useMutation({
    mutationFn: (id: string) => b2gApi.liftSuppression(id),
    onSuccess: () => {
      toast.ok("Suppression lifted.");
      return invalidate();
    },
    onError: (err) => toast.error(err),
  });

  return { waves, wave, plan, markets, suppressions, waveAction, decide, lift, busy };
}

export type WavesData = ReturnType<typeof useWavesData>;
